import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";

import {
  CODE_TO_USAGE_TYPE,
  QUESTIONNAIRE_TITLE_EXTENSION_URL,
  QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
} from "src-next/modules/questionnaire-builder/form-builder/utils/questionnaireQuestionnaireConstants";

import type { QuestionnaireUsageTypeDomain } from "../transformers/extractQuestionnaireResponsesFromSummary";
import {
  normalizeQuestionnaireLogicalIdForComparison,
  normalizeQuestionnaireReferenceToLogicalId,
} from "../transformers/questionnaireResponseExclusions";
import { patientQuestionnairesScrollbarSx } from "../utils/patientQuestionnairesScrollbarSx";
import { QuestionnaireProtocolLinkChips } from "@/components/common/QuestionnaireProtocolLinkChips";
import { useOrganizationProtocolQuestionnaireIndex } from "@/services/fhir/protocol/hooks/useOrganizationProtocolQuestionnaireIndex";
import { useFhirData } from "@/utils/useFhirData";

export interface PatientQuestionnairesDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Bundle Patient Summary já carregado no atendimento.
   * Este dialog não deve reconsultar o Patient Summary.
   */
  summaryData: fhir5.Bundle | undefined;
  /**
   * Ids lógicos de questionários a ocultar (ex.: aba Dados objetivos).
   * O registro clínico (`VITE_QUESTIONNAIRE_ID`) já é filtrado no módulo patient-summary.
   */
  excludeQuestionnaireLogicalIds?: readonly string[];
  /**
   * Referência do questionário (ex.: `Questionnaire/<id>`) para abrir direto no detalhe
   * da resposta mais recente compatível, quando existir no summary.
   */
  initialQuestionnaireReference?: string;
  /**
   * Instante ISO para desambiguar quando há várias `QuestionnaireResponse` do mesmo
   * questionário: escolhe a linha cujo `authored` está mais próximo no tempo deste
   * valor (ex.: `Task.meta.lastUpdated` ao concluir o envio). Opcional; sem âncora ou
   * com um único match, mantém-se a seleção pela resposta mais recente por `authored`.
   */
  initialQuestionnaireResponseAnchorIso?: string;
}

type UsageTabValue = "all" | QuestionnaireUsageTypeDomain;

const USAGE_TABS: { value: UsageTabValue; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "triage", label: "Triagem" },
  { value: "preConsultation", label: "Pré-consulta" },
  { value: "postConsultation", label: "Pós-consulta" },
  { value: "attendance", label: "Acompanhamento" },
];

const TOUCH_MIN_PX = 44;

type QaPair = {
  linkId?: string;
  question: string;
  questionHelper?: string;
  answer: string;
};

type PatientQuestionnaireRow = {
  id: string;
  questionnaireResponse: fhir5.QuestionnaireResponse;
  title: string;
  usageTypeDomain?: QuestionnaireUsageTypeDomain;
  usageTypeLabel: string;
  authoredDisplay: string;
  authoredAtIso?: string;
  responseIdentifierValue?: string;
  qaPairs: QaPair[];
};

function comparePatientQuestionnaireRowsByAuthoredDesc(
  a: PatientQuestionnaireRow,
  b: PatientQuestionnaireRow,
): number {
  const ta = new Date(a.authoredAtIso ?? 0).getTime();
  const tb = new Date(b.authoredAtIso ?? 0).getTime();
  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;
  return tb - ta;
}

const AUTHORED_DATE_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatQuestionnaireAuthoredDateLabel(
  authoredIso: string | undefined | null,
): string {
  if (authoredIso == null) return "—";
  const t = String(authoredIso).trim();
  if (!t || t === "—") return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return t;
  return AUTHORED_DATE_FORMATTER.format(d);
}

function primaryResponseIdentifierValue(
  qr: fhir5.QuestionnaireResponse,
): string | undefined {
  const idents = qr.identifier;
  if (!idents?.length) return undefined;
  const v = idents[0]?.value?.trim();
  return v && v.length > 0 ? v : undefined;
}

function questionnaireReferenceString(qr: fhir5.QuestionnaireResponse): string {
  const ref =
    typeof qr.questionnaire === "string"
      ? qr.questionnaire
      : (qr.questionnaire as { reference?: string } | undefined)?.reference;
  return String(ref ?? "").trim();
}

function questionnaireTitleFromResponse(
  qr: fhir5.QuestionnaireResponse,
): string | null {
  const extensions = qr.extension;
  if (!Array.isArray(extensions)) return null;
  const ext = extensions.find(
    (e: any) => e?.url === QUESTIONNAIRE_TITLE_EXTENSION_URL,
  );
  const v = String((ext as any)?.valueString ?? "").trim();
  return v ? v : null;
}

function usageTypeFromQuestionnaireResponse(qr: fhir5.QuestionnaireResponse): {
  usageTypeDomain: QuestionnaireUsageTypeDomain | undefined;
  usageTypeLabel: string;
} {
  const extensions = qr.extension;
  if (!Array.isArray(extensions))
    return { usageTypeDomain: undefined, usageTypeLabel: "—" };

  const ext = extensions.find(
    (extension: any) =>
      extension?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  );
  if (!ext) return { usageTypeDomain: undefined, usageTypeLabel: "—" };

  const codeRaw =
    ext?.valueCode != null && String(ext.valueCode).trim() !== ""
      ? String(ext.valueCode).trim()
      : ext?.valueCoding?.code != null &&
          String(ext.valueCoding.code).trim() !== ""
        ? String(ext.valueCoding.code).trim()
        : null;

  if (codeRaw) {
    const domain = CODE_TO_USAGE_TYPE[codeRaw] as
      | QuestionnaireUsageTypeDomain
      | undefined;
    if (domain) {
      const label =
        USAGE_TABS.find((t) => t.value === domain)?.label ?? codeRaw;
      return { usageTypeDomain: domain, usageTypeLabel: label };
    }
    return { usageTypeDomain: undefined, usageTypeLabel: codeRaw };
  }

  const display = ext?.valueCoding?.display?.trim();
  if (display) return { usageTypeDomain: undefined, usageTypeLabel: display };

  const valueString = ext?.valueString?.trim();
  if (valueString)
    return { usageTypeDomain: undefined, usageTypeLabel: valueString };

  return { usageTypeDomain: undefined, usageTypeLabel: "—" };
}

function formatAnswerLine(
  answer: fhir5.QuestionnaireResponseItemAnswer,
): string {
  if (answer.valueString !== undefined && answer.valueString !== null) {
    return String(answer.valueString);
  }
  if (answer.valueBoolean !== undefined && answer.valueBoolean !== null) {
    return answer.valueBoolean ? "Sim" : "Não";
  }
  if (answer.valueCoding) {
    return (answer.valueCoding.display ?? answer.valueCoding.code ?? "").trim();
  }
  return "";
}

function extractQaPairs(qr: fhir5.QuestionnaireResponse): QaPair[] {
  const pairs: QaPair[] = [];

  const walkItems = (items: fhir5.QuestionnaireResponseItem[] | undefined) => {
    for (const item of items ?? []) {
      if (item.item?.length) {
        walkItems(item.item);
        continue;
      }

      const answers = (item.answer ?? [])
        .map(formatAnswerLine)
        .filter((s) => s.length > 0);

      if (answers.length === 0 && !item.text) continue;
      const linkId = item.linkId?.trim();
      pairs.push({
        linkId: linkId || undefined,
        question: item.text?.trim() || linkId || "Pergunta",
        answer: answers.join(", ") || "—",
      });
    }
  };

  walkItems(qr.item);
  return pairs;
}

function QuestionAnswerBlock(props: {
  questionnaireId: string;
  index: number;
  question: string;
  questionHelper?: string;
  answer: string;
}) {
  const theme = useTheme();
  const titleId = `patient-q-${props.questionnaireId}-q-${props.index}-title`;

  const answerSurfaceSx = useMemo(
    () => ({
      mt: 1.5,
      p: 1.5,
      borderRadius: 1,
      border: 1,
      borderColor: "divider",
      bgcolor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.06)
          : theme.palette.grey[50],
    }),
    [theme],
  );

  return (
    <Card
      variant="outlined"
      component="article"
      aria-labelledby={titleId}
      sx={{
        borderRadius: 2,
        borderColor: "divider",
      }}
    >
      <CardContent
        sx={{
          p: 2,
          "&:last-child": { pb: 2 },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <HelpOutlineIcon
            sx={{
              fontSize: 20,
              color: "primary.main",
              opacity: 0.85,
              mt: 0.125,
              flexShrink: 0,
            }}
            aria-hidden
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id={titleId}
              component="h3"
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {props.question}
            </Typography>
            {props.questionHelper?.trim() ? (
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
                sx={{ mt: 0.75, display: "block", lineHeight: 1.5 }}
              >
                {props.questionHelper.trim()}
              </Typography>
            ) : null}

            <Box sx={answerSurfaceSx}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <ChatBubbleOutlineIcon
                  sx={{
                    fontSize: 18,
                    color: "text.secondary",
                    mt: 0.125,
                    flexShrink: 0,
                    opacity: 0.9,
                  }}
                  aria-hidden
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="p"
                  sx={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}
                >
                  {props.answer}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * Lista questionários respondidos no atendimento (Patient Summary MHD + enriquecimento Questionnaire).
 */
const PatientQuestionnairesDialog = memo(
  ({
    open,
    onClose,
    summaryData,
    excludeQuestionnaireLogicalIds,
    initialQuestionnaireReference,
    initialQuestionnaireResponseAnchorIso,
  }: PatientQuestionnairesDialogProps) => {
    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

    const scrollbarSx = useMemo(
      () => patientQuestionnairesScrollbarSx(theme),
      [theme],
    );

    const [usageTab, setUsageTab] = useState<UsageTabValue>("all");
    const [selected, setSelected] = useState<PatientQuestionnaireRow | null>(
      null,
    );
    const initialSelectionAppliedRef = useRef(false);

    const { status: protocolIndexStatus, getProtocolNamesForQuestionnaire } =
      useOrganizationProtocolQuestionnaireIndex({ enabled: open });

    useEffect(() => {
      if (!open) {
        setSelected(null);
        setUsageTab("all");
        initialSelectionAppliedRef.current = false;
      }
    }, [open]);

    const excludeSetKey = useMemo(() => {
      if (!excludeQuestionnaireLogicalIds?.length) return "";
      return [...excludeQuestionnaireLogicalIds]
        .map((id) => normalizeQuestionnaireLogicalIdForComparison(id) ?? "")
        .filter(Boolean)
        .sort()
        .join("|");
    }, [excludeQuestionnaireLogicalIds]);

    const excludeSet = useMemo(() => {
      if (!excludeSetKey) return new Set<string>();
      return new Set(excludeSetKey.split("|"));
    }, [excludeSetKey]);

    const hasSummary = Boolean(summaryData);

    const rows = useMemo((): PatientQuestionnaireRow[] => {
      if (!summaryData) return [];

      const questionnaireSubBundles = useFhirData.extractPatientSummaryResource(
        summaryData,
        "QuestionnaireResponse",
      ) as Array<{ entry?: any[] }> | undefined;

      const entries = (questionnaireSubBundles ?? [])
        .flatMap((b) => (Array.isArray(b?.entry) ? b.entry : []))
        .filter(Boolean);

      const productEntries = entries.filter((bundleEntry: any) => {
        const extensions = bundleEntry?.resource?.extension;
        if (!Array.isArray(extensions)) return false;
        return extensions.some(
          (extension: any) =>
            extension?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL &&
            ((extension?.valueCode != null &&
              String(extension.valueCode).trim() !== "") ||
              (extension?.valueCoding?.code != null &&
                String(extension.valueCoding.code).trim() !== "") ||
              (extension?.valueCoding?.display != null &&
                String(extension.valueCoding.display).trim() !== "")),
        );
      });

      const qrs = productEntries
        .map((e: any) => e?.resource)
        .filter(
          (r: any): r is fhir5.QuestionnaireResponse =>
            r && r.resourceType === "QuestionnaireResponse",
        );

      const filteredByExclude = qrs.filter((qr) => {
        if (!excludeSet.size) return true;
        const ref = questionnaireReferenceString(qr);
        const logicalIdRaw = ref.includes("/") ? ref.split("/")[1] : ref;
        const normalized =
          normalizeQuestionnaireLogicalIdForComparison(logicalIdRaw);
        if (!normalized) return true;
        return !excludeSet.has(normalized);
      });

      return filteredByExclude.map((qr) => {
        const authoredRaw = String(qr.authored ?? "").trim();
        const usage = usageTypeFromQuestionnaireResponse(qr);

        const titleFromExt = questionnaireTitleFromResponse(qr);
        const titleFallback = questionnaireReferenceString(qr);
        const title = titleFromExt || titleFallback || "Questionário";

        return {
          id: qr.id ?? `${title}-${authoredRaw}`,
          questionnaireResponse: qr,
          title,
          ...(usage.usageTypeDomain
            ? { usageTypeDomain: usage.usageTypeDomain }
            : {}),
          usageTypeLabel: usage.usageTypeLabel,
          authoredDisplay: formatQuestionnaireAuthoredDateLabel(authoredRaw),
          ...(authoredRaw ? { authoredAtIso: authoredRaw } : {}),
          ...(primaryResponseIdentifierValue(qr)
            ? { responseIdentifierValue: primaryResponseIdentifierValue(qr) }
            : {}),
          qaPairs: extractQaPairs(qr),
        };
      });
    }, [excludeSet, summaryData]);

    useEffect(() => {
      if (!open) return;
      const refRaw = initialQuestionnaireReference?.trim();
      if (!refRaw) return;
      if (rows.length === 0) return;
      if (initialSelectionAppliedRef.current) return;

      const target = normalizeQuestionnaireReferenceToLogicalId(refRaw);
      initialSelectionAppliedRef.current = true;
      if (!target) return;

      const matching = rows.filter((row) => {
        const rowLogical = normalizeQuestionnaireReferenceToLogicalId(
          row.questionnaireResponse.questionnaire,
        );
        return rowLogical != null && rowLogical === target;
      });
      if (matching.length === 0) return;

      const anchorRaw = initialQuestionnaireResponseAnchorIso?.trim();
      const anchorMs =
        anchorRaw && anchorRaw.length > 0 ? Date.parse(anchorRaw) : NaN;
      const useAnchorProximity =
        matching.length > 1 &&
        Boolean(anchorRaw) &&
        !Number.isNaN(anchorMs);

      if (useAnchorProximity) {
        matching.sort((a, b) => {
          const ta = Date.parse(a.authoredAtIso ?? "");
          const tb = Date.parse(b.authoredAtIso ?? "");
          const da = Number.isNaN(ta)
            ? Number.POSITIVE_INFINITY
            : Math.abs(ta - anchorMs);
          const db = Number.isNaN(tb)
            ? Number.POSITIVE_INFINITY
            : Math.abs(tb - anchorMs);
          if (da !== db) return da - db;
          return comparePatientQuestionnaireRowsByAuthoredDesc(a, b);
        });
      } else {
        matching.sort(comparePatientQuestionnaireRowsByAuthoredDesc);
      }

      setUsageTab("all");
      setSelected(matching[0] ?? null);
    }, [
      open,
      rows,
      initialQuestionnaireReference,
      initialQuestionnaireResponseAnchorIso,
    ]);

    const filteredRows = useMemo(() => {
      if (usageTab === "all") return rows;
      return rows.filter((row) => row.usageTypeDomain === usageTab);
    }, [rows, usageTab]);

    const handleClose = useCallback(() => {
      setSelected(null);
      setUsageTab("all");
      onClose();
    }, [onClose]);

    const handleBackToList = useCallback(() => {
      setSelected(null);
    }, []);

    const paperSx = useMemo(
      () => ({
        maxHeight: isSmDown ? "100%" : { xs: "90vh", sm: "85vh" },
        height: isSmDown ? "100%" : "auto",
        display: "flex",
        flexDirection: "column" as const,
      }),
      [isSmDown],
    );

    const tabSx = useMemo(
      () => ({
        minHeight: TOUCH_MIN_PX,
        textTransform: "none" as const,
        ...theme.typography.body2,
        px: 1.5,
      }),
      [theme.typography.body2],
    );

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        fullScreen={isSmDown}
        maxWidth="md"
        aria-labelledby="patient-questionnaires-dialog-title"
        scroll="paper"
        PaperProps={{ sx: paperSx }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            id="patient-questionnaires-dialog-title"
            variant="subtitle1"
            fontWeight={700}
            component="span"
          >
            Questionários do paciente
          </Typography>
          <IconButton
            aria-label="Fechar"
            edge="end"
            onClick={handleClose}
            size="medium"
            sx={{
              color: "text.secondary",
              "&:focus-visible": {
                outline: 2,
                outlineOffset: 2,
                outlineColor: "primary.main",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            px: { xs: 2, sm: 3 },
            py: 2,
            ...scrollbarSx,
          }}
        >
          {!hasSummary && (
            <Alert
              severity="info"
              sx={{ mb: 2, borderRadius: 2 }}
              variant="outlined"
            >
              O resumo do paciente (`summaryData`) ainda não está disponível
              neste atendimento. Sem ele, não é possível listar respostas de
              questionários.
            </Alert>
          )}

          {hasSummary && rows.length === 0 && (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma resposta de questionário do produto encontrada neste
                  resumo.
                </Typography>
              </CardContent>
            </Card>
          )}

          {hasSummary && rows.length > 0 && (
            <>
              {!selected && (
                <Card
                  variant="outlined"
                  sx={{ borderRadius: 2, width: "100%" }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      p: { xs: 1.5, sm: 2 },
                      "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                    }}
                  >
                    <Stack spacing={2}>
                      <Tabs
                        value={usageTab}
                        onChange={(_e, next: UsageTabValue) =>
                          setUsageTab(next)
                        }
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                          minHeight: TOUCH_MIN_PX,
                          "& .MuiTabScrollButton-root": {
                            minWidth: TOUCH_MIN_PX,
                          },
                        }}
                      >
                        {USAGE_TABS.map((tab) => (
                          <Tab
                            key={tab.value}
                            label={tab.label}
                            value={tab.value}
                            sx={tabSx}
                          />
                        ))}
                      </Tabs>

                      <Divider />

                      <List dense disablePadding>
                        {filteredRows.map((row) => (
                          <ListItem
                            key={row.id}
                            disablePadding
                            sx={{ mb: 0.5 }}
                          >
                            <ListItemButton
                              onClick={() => setSelected(row)}
                              sx={{
                                minHeight: TOUCH_MIN_PX,
                                alignItems: "flex-start",
                                py: 1.25,
                                px: 1.5,
                                borderRadius: 2,
                                border: 1,
                                borderColor: "transparent",
                                "&:hover": {
                                  borderColor: "divider",
                                  bgcolor: "action.hover",
                                },
                              }}
                            >
                              <ListItemText
                                primaryTypographyProps={{
                                  component: "div",
                                }}
                                secondaryTypographyProps={{
                                  component: "div",
                                }}
                                primary={
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                    sx={{ minWidth: 0, gap: 1 }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      component="span"
                                      sx={{
                                        fontWeight: 700,
                                        flex: 1,
                                        minWidth: 0,
                                        color: "text.primary",
                                      }}
                                    >
                                      {row.title}
                                    </Typography>
                                    <Chip
                                      label={row.usageTypeLabel}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        flexShrink: 0,
                                        height: 28,
                                        maxWidth: "42%",
                                      }}
                                    />
                                  </Stack>
                                }
                                secondary={
                                  <Stack
                                    component="div"
                                    spacing={0.25}
                                    sx={{ mt: 0.75 }}
                                  >
                                    <Typography
                                      component="time"
                                      variant="body2"
                                      dateTime={row.authoredAtIso}
                                      sx={{
                                        fontWeight: 600,
                                        color: "text.primary",
                                        display: "block",
                                      }}
                                    >
                                      {row.authoredDisplay}
                                    </Typography>
                                    {row.responseIdentifierValue ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block" }}
                                      >
                                        Identificador:{" "}
                                        {row.responseIdentifierValue}
                                      </Typography>
                                    ) : null}
                                    {row.usageTypeDomain === "postConsultation" ? (
                                      <QuestionnaireProtocolLinkChips
                                        protocolNames={getProtocolNamesForQuestionnaire(
                                          questionnaireReferenceString(
                                            row.questionnaireResponse,
                                          ),
                                        )}
                                        status={protocolIndexStatus}
                                      />
                                    ) : null}
                                  </Stack>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}

                        {filteredRows.length === 0 && (
                          <ListItem>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Nenhum questionário nesta categoria.
                                </Typography>
                              }
                            />
                          </ListItem>
                        )}
                      </List>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {selected && (
                <Stack spacing={2}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                          sx={{ minWidth: 0, flex: 1, gap: 1 }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ flex: 1, minWidth: 0 }}
                            component="h2"
                          >
                            {selected.title}
                          </Typography>
                          <Chip
                            label={selected.usageTypeLabel}
                            size="small"
                            variant="outlined"
                            sx={{ flexShrink: 0, height: 28 }}
                          />
                        </Stack>

                        <Stack spacing={0.25}>
                          <Typography
                            component="time"
                            variant="body1"
                            dateTime={selected.authoredAtIso}
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {selected.authoredDisplay}
                          </Typography>
                          {selected.responseIdentifierValue ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="p"
                            >
                              Identificador: {selected.responseIdentifierValue}
                            </Typography>
                          ) : null}
                          {selected.usageTypeDomain === "postConsultation" ? (
                            <QuestionnaireProtocolLinkChips
                              protocolNames={getProtocolNamesForQuestionnaire(
                                questionnaireReferenceString(
                                  selected.questionnaireResponse,
                                ),
                              )}
                              status={protocolIndexStatus}
                            />
                          ) : null}
                        </Stack>

                        {/* Descrição do Questionnaire não está disponível via summaryData sem lookup/enrichment. */}
                      </Stack>
                    </CardContent>
                  </Card>

                  <Stack
                    spacing={1.5}
                    component="section"
                    aria-label="Respostas"
                  >
                    {selected.qaPairs.map((pair, idx) => (
                      <QuestionAnswerBlock
                        key={`${selected.id}-q-${idx}`}
                        questionnaireId={selected.id}
                        index={idx}
                        question={pair.question}
                        questionHelper={pair.questionHelper}
                        answer={pair.answer}
                      />
                    ))}

                    {selected.qaPairs.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Não há itens de resposta neste questionário.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {selected ? (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToList}
              sx={{
                minHeight: TOUCH_MIN_PX,
                textTransform: "none",
              }}
            >
              Voltar
            </Button>
          ) : null}
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleClose}
            sx={{ minHeight: TOUCH_MIN_PX, textTransform: "none", px: 2.5 }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

PatientQuestionnairesDialog.displayName = "PatientQuestionnairesDialog";

export { PatientQuestionnairesDialog };
